import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

const sizeClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
  full: "max-w-3xl",
} as const;

type ModalSize = keyof typeof sizeClasses;

interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

interface ModalContentProps {
  size?: ModalSize;
  /** Visible dialog title rendered in the header. */
  title?: string;
  /** Visible dialog description rendered under the title. */
  description?: string;
  /** Accessible name for screen readers when no visible `title` is shown.
   * Radix Dialog REQUIRES a Title; without one it logs a dev-mode warning
   * and assistive tech announces the dialog as anonymous. When a caller
   * wants a chromeless dialog (e.g. the command-K search modal), pass
   * `ariaLabel` and the title gets rendered into a visually-hidden slot. */
  ariaLabel?: string;
  children: React.ReactNode;
  className?: string;
  hideCloseButton?: boolean;
}

function Modal({ open, onOpenChange, children }: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {children}
    </Dialog.Root>
  );
}

function ModalContent({
  size = "md",
  title,
  description,
  ariaLabel,
  children,
  className,
  hideCloseButton = false,
}: ModalContentProps) {
  // Radix Dialog *requires* a Title for accessibility (WCAG 4.1.2). When
  // the caller doesn't render a visible header — confirmation dialogs,
  // command palettes, fullscreen interstitials — we still must mount one
  // so the assistive-tech announcement has a name. The visually-hidden
  // copy falls back to ariaLabel, then to a generic last-resort string.
  // Generic strings are deliberately bland; the lint we'd want here is
  // "give your modal a real name", not "let the framework apologize for
  // you", so callers should always pass ariaLabel when no title.
  const accessibleName = title ?? ariaLabel ?? "Dialog";

  return (
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
      <Dialog.Content
        className={cn(
          "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-xl shadow-xl w-full p-0",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "max-h-[90vh] flex flex-col mx-4",
          sizeClasses[size],
          className
        )}
      >
        {title ? (
          // Visible header — Title + optional Description + close X.
          <div className="flex items-center justify-between p-4 pb-0">
            <div>
              <Dialog.Title className="text-lg font-semibold text-slate-900">
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="text-sm text-slate-500 mt-1">
                  {description}
                </Dialog.Description>
              )}
            </div>
            {!hideCloseButton && (
              <Dialog.Close
                // text-slate-500 (was -400): the 400 weight sat at ~3.0:1
                // on white — right at the edge of WCAG 2.2 SC 1.4.11.
                // 500 is ~4.6:1 and gives margin against background blur.
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors -mr-2 -mt-2"
                aria-label="Close dialog"
              >
                <X className="w-4 h-4 text-slate-500" aria-hidden="true" />
              </Dialog.Close>
            )}
          </div>
        ) : (
          // No visible header — emit a sr-only Title for AT and a sr-only
          // close button so keyboard users can still escape via Tab + Enter
          // even when there's no visible close UI. The Esc key continues to
          // work through Radix regardless.
          <>
            <Dialog.Title className="sr-only">{accessibleName}</Dialog.Title>
            {description && (
              <Dialog.Description className="sr-only">
                {description}
              </Dialog.Description>
            )}
          </>
        )}
        <div className="flex-1 overflow-y-auto">{children}</div>
      </Dialog.Content>
    </Dialog.Portal>
  );
}

function ModalClose({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Dialog.Close className={className} asChild>
      {children}
    </Dialog.Close>
  );
}

Modal.Content = ModalContent;
Modal.Close = ModalClose;

export { Modal };
export type { ModalSize };
